<script setup lang="ts">
import TripCard from '../TripCard/TripCard.vue';
// eslint-disable-next-line no-restricted-imports
import PriceActions from '../../price-actions/PriceActions.vue';
import ServiceOfferCard from '../ServiceOfferCard.vue';
import CardSection from '@components/ui/hotel&view/card-section/sections/CardSection.vue';
import type TripPackage from '@itinerary/trip/TripPackage';
import { extraInfoIconMapper } from '@itinerary/types';
import { useSingleLayer } from '@composable';
import { BannerSearchResultEnum } from '@clients/ContentApi';

const {
    list = [],
    title,
    displayPriceTrack,
    displayTaxiOffer,
} = defineProps<IProps>();
const emit = defineEmits<{
    buttonClicked: [item: TripPackage, index: number];
    viewSection: [];
}>();
interface IProps {
    title?: string;
    list?: TripPackage[];
    displayBanners?: boolean;
    displayPriceTrack?: boolean;
    displayTaxiOffer?: boolean;
    realTimeLoading?: boolean;
    compactCards?: boolean;
    disableDetails?: boolean;
}

const PackageDetails = defineAsyncComponent(
    () => import('../TripCard/_components/package-details/PackageDetails.vue')
);

const { isMobile } = useSize();
const route = useRoute();
const contentStore = useContentStore();
const siteConfigs = useSiteConfigs();
const currentShowDetailsIndex = ref<number>();
const priceTrackIndex = computed(() =>
    list.length >= 3 ? 2 : list.length < 3 ? list.length - 1 : 0
);

const taxiBanner = computed(() => {
    if (!siteConfigs.showBanners) {
        return null;
    }
    return (
        contentStore.taxiBanners
            .filter(b => new RegExp(b.urlPattern || '').test(route.path))
            .filter(banner => {
                switch (banner.searchResult) {
                    case BannerSearchResultEnum.Both:
                        return true;
                    case BannerSearchResultEnum.Empty:
                        return !list.length;
                    case BannerSearchResultEnum.NonEmpty:
                        return !!list.length;
                    default:
                        return true;
                }
            })
            .filter(
                m =>
                    !m.displaySize ||
                    m.displaySize === 'both' ||
                    (m.displaySize === 'mobile'
                        ? isMobile.value
                        : !isMobile.value)
            )[0] ?? null
    );
});

const hotelBanner = computed(() => {
    if (!siteConfigs.showBanners) {
        return null;
    }
    return (
        contentStore.hotelBanners
            .filter(b => new RegExp(b.urlPattern || '').test(route.path))
            .filter(banner => {
                switch (banner.searchResult) {
                    case BannerSearchResultEnum.Both:
                        return true;
                    case BannerSearchResultEnum.Empty:
                        return !list.length;
                    case BannerSearchResultEnum.NonEmpty:
                        return !!list.length;
                    default:
                        return true;
                }
            })
            .filter(
                m =>
                    !m.displaySize ||
                    m.displaySize === 'both' ||
                    (m.displaySize === 'mobile'
                        ? isMobile.value
                        : !isMobile.value)
            )[0] ?? null
    );
});

const handleModalReserve = (pkg: TripPackage, index: number) => {
    if (!pkg.reserveButtonEnabled && !pkg.autoReserveEnabled) {
        swal({
            icon: 'warning',
            title: pkg?.alertText,
            confirmButtonText: 'باشه',
            text: pkg.reserveError,
        });
        return;
    }
    closeModal();
    emit('buttonClicked', pkg, index);
};

const { open: openModal, close: closeModal } = useSingleLayer();

const onUpdateDetails = (
    value: boolean,
    index: number,
    tripPackage: TripPackage
) => {
    if (value) {
        currentShowDetailsIndex.value = index;
        if (isMobile.value) {
            openModal({
                component: PackageDetails,
                props: {
                    tripPackage,
                    onReserve: () => {
                        handleModalReserve(tripPackage, index);
                    },
                    onDetailClosed: () => {
                        currentShowDetailsIndex.value = undefined;
                    },
                    style: {
                        '--max-height': `${(tripPackage?.allGroupedTrips?.length ?? 1) * 115 + 200}px`,
                        zIndex: 100000000,
                    },
                },
            });
        }
    } else if (currentShowDetailsIndex.value === index) {
        currentShowDetailsIndex.value = undefined;
        if (isMobile.value) {
            closeModal();
        }
    }
};

watch(
    () => list,
    () => {
        currentShowDetailsIndex.value = undefined;
    }
);
</script>

<template>
    <CardSection
        :title
        :display-banners
        :list
        :real-time-loading
        @view-section="emit('viewSection')"
    >
        <template #card="{ item, index, mouseenter, mouseleave }">
            <TripCard
                :details="currentShowDetailsIndex === index"
                :trip-package="item"
                :is-open-details="currentShowDetailsIndex === index"
                :compact="compactCards"
                :disable-details
                @update:details="onUpdateDetails($event, index, item)"
                @button-clicked="emit('buttonClicked', $event, index)"
                @mouseenter="mouseenter"
                @mouseleave="mouseleave"
            >
                <template #highlight>
                    <slot name="card-highlight" :trip="item" />
                </template>
                <template v-if="item?.extraInfo?.text" #extra-detail>
                    <div class="baggage-container">
                        <mr-divider
                            vertical
                            :thickness="1"
                            color="var(--blue-100)"
                            class="baggage-divider mobile-only"
                        />
                        <mr-icon
                            v-if="item?.extraInfo.icon"
                            :icon="extraInfoIconMapper[item?.extraInfo.icon]"
                            :size="{
                                mobile: 'small',
                                desktop: 'medium',
                            }"
                            color="var(--cool-gray-700)"
                        />
                        <mr-typography
                            size="small"
                            weight="500"
                            color="var(--cool-gray-700)"
                            class="baggage"
                            white-space="nowrap"
                        >
                            {{ item?.extraInfo.text }}
                        </mr-typography>
                    </div>
                </template>
            </TripCard>
        </template>
        <template #extra-item>
            <slot name="extra-item" />
            <PriceActions
                v-if="displayPriceTrack"
                key="forecastTrack"
                :style="{ order: priceTrackIndex }"
                mobile-only
            />
            <template v-if="displayTaxiOffer">
                <ServiceOfferCard
                    v-if="taxiBanner || hotelBanner"
                    key="taxi-offer-card"
                    :banner="taxiBanner ? taxiBanner : hotelBanner"
                />
                <ServiceOfferCard
                    v-if="taxiBanner && hotelBanner"
                    key="hotel-offer-card"
                    :banner="hotelBanner"
                    :style="{ order: 5 }"
                />
            </template>
        </template>
    </CardSection>
</template>

<style scoped lang="scss">
.baggage-container {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 0;
    .baggage-divider {
        height: 24px;
    }
    .baggage {
        max-width: 100px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    @include desktop-only {
        align-items: flex-start;
        height: 48px;
        padding: 4px 0;
    }
}
</style>
